// frontend/src/pages/auditoria/AuditoriaList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ClearAll as ClearAllIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import auditoriaService from '../../services/auditoriaService';
import { useAuth } from '../../hooks/useAuth';

const AuditoriaList = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados de paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Estados de filtros
  const [filtros, setFiltros] = useState({
    usuario_id: '',
    accion: '',
    tabla: '',
    fecha_inicio: null,
    fecha_fin: null,
    search: ''
  });
  
  // Estados para opciones de filtros
  const [usuarios, setUsuarios] = useState([]);
  const [tablas, setTablas] = useState([]);
  
  // Estados del modal de detalle
  const [selectedLog, setSelectedLog] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  
  // Estados de la interfaz
  const [showFilters, setShowFilters] = useState(false);

  // Verificar permisos
  useEffect(() => {
    if (user && user.rol !== 'Administrador') {
      setError('Acceso denegado. Solo administradores pueden ver la auditoría.');
      setLoading(false);
      return;
    }
  }, [user]);

  // Cargar datos iniciales
  useEffect(() => {
    if (user && user.rol === 'Administrador') {
      cargarDatosIniciales();
    }
  }, [user]);

  // Cargar logs cuando cambien los filtros o la paginación
  useEffect(() => {
    if (user && user.rol === 'Administrador') {
      cargarLogs();
    }
  }, [page, rowsPerPage, filtros, user]);

  const cargarDatosIniciales = async () => {
    try {
      const [usuariosRes, tablasRes] = await Promise.all([
        auditoriaService.getUsuariosParaFiltro(),
        auditoriaService.getTablasParaFiltro()
      ]);
      
      setUsuarios(usuariosRes.data.data || []);
      setTablas(tablasRes.data.data || []);
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
    }
  };

  const cargarLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1, // API usa base 1, componente usa base 0
        limit: rowsPerPage,
        ...filtros,
        // Formatear fechas para la API
        fecha_inicio: filtros.fecha_inicio ? format(filtros.fecha_inicio, 'yyyy-MM-dd') : undefined,
        fecha_fin: filtros.fecha_fin ? format(filtros.fecha_fin, 'yyyy-MM-dd') : undefined
      };

      // Remover parámetros vacíos
      Object.keys(params).forEach(key => {
        if (!params[key]) {
          delete params[key];
        }
      });

      const response = await auditoriaService.getLogs(params);
      
      if (response.data.success) {
        setLogs(response.data.data.logs || []);
        setTotalCount(response.data.data.pagination?.total || 0);
      } else {
        setError('Error al cargar los logs de auditoría');
      }
    } catch (error) {
      console.error('Error cargando logs:', error);
      setError('Error al cargar los datos. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filtros]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
    setPage(0); // Resetear a la primera página
  };

  const limpiarFiltros = () => {
    setFiltros({
      usuario_id: '',
      accion: '',
      tabla: '',
      fecha_inicio: null,
      fecha_fin: null,
      search: ''
    });
    setPage(0);
  };

  const verDetalle = async (logId) => {
    try {
      const response = await auditoriaService.getDetalleLog(logId);
      if (response.data.success) {
        setSelectedLog(response.data.data);
        setOpenDetailModal(true);
      }
    } catch (error) {
      console.error('Error al obtener detalle del log:', error);
    }
  };

  const exportarDatos = async () => {
    try {
      await auditoriaService.exportarLogs(filtros);
    } catch (error) {
      console.error('Error al exportar datos:', error);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return format(new Date(fecha), 'dd/MM/yyyy HH:mm', { locale: es });
  };

  const getColorChip = (accion) => {
    if (accion.toLowerCase().includes('registró') || accion.toLowerCase().includes('creó')) {
      return 'success';
    } else if (accion.toLowerCase().includes('modificó') || accion.toLowerCase().includes('actualizó')) {
      return 'warning';
    } else if (accion.toLowerCase().includes('eliminó')) {
      return 'error';
    } else {
      return 'info';
    }
  };

  if (error && error.includes('Acceso denegado')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Auditoría del Sistema
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Registro de todas las acciones realizadas por los usuarios en el sistema
          </Typography>
        </Box>

        {/* Filtros */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Filtros</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  startIcon={<FilterIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outlined"
                  size="small"
                >
                  {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
                </Button>
                <Button
                  startIcon={<ClearAllIcon />}
                  onClick={limpiarFiltros}
                  variant="outlined"
                  size="small"
                >
                  Limpiar
                </Button>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={cargarLogs}
                  variant="outlined"
                  size="small"
                  disabled={loading}
                >
                  Actualizar
                </Button>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={exportarDatos}
                  variant="outlined"
                  size="small"
                >
                  Exportar
                </Button>
              </Stack>
            </Box>

            {/* Búsqueda rápida siempre visible */}
            <TextField
              fullWidth
              label="Búsqueda rápida"
              variant="outlined"
              value={filtros.search}
              onChange={(e) => handleFiltroChange('search', e.target.value)}
              placeholder="Buscar por acción, IP, tabla..."
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
              sx={{ mb: showFilters ? 2 : 0 }}
            />

            {/* Filtros avanzados */}
            {showFilters && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Usuario</InputLabel>
                    <Select
                      value={filtros.usuario_id}
                      label="Usuario"
                      onChange={(e) => handleFiltroChange('usuario_id', e.target.value)}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {usuarios.map((usuario) => (
                        <MenuItem key={usuario.id} value={usuario.id}>
                          {usuario.nombre} {usuario.apellidos} ({usuario.username})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Tabla</InputLabel>
                    <Select
                      value={filtros.tabla}
                      label="Tabla"
                      onChange={(e) => handleFiltroChange('tabla', e.target.value)}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {tablas.map((tabla) => (
                        <MenuItem key={tabla} value={tabla}>
                          {tabla}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Fecha inicio"
                    value={filtros.fecha_inicio}
                    onChange={(date) => handleFiltroChange('fecha_inicio', date)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    maxDate={new Date()}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Fecha fin"
                    value={filtros.fecha_fin}
                    onChange={(date) => handleFiltroChange('fecha_fin', date)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    maxDate={new Date()}
                    minDate={filtros.fecha_inicio}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Tabla de logs */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            )}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Acción</TableCell>
                    <TableCell>Tabla</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No se encontraron registros de auditoría
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          {formatearFecha(log.fecha)}
                        </TableCell>
                        <TableCell>
                          {log.usuario ? (
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {log.usuario.nombre} {log.usuario.apellidos}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {log.usuario.username}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Usuario desconocido
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.accion}
                            color={getColorChip(log.accion)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {log.tabla || '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {log.ip || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver detalle">
                            <IconButton
                              size="small"
                              onClick={() => verDetalle(log.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </CardContent>
        </Card>

        {/* Modal de detalle */}
        <Dialog
          open={openDetailModal}
          onClose={() => setOpenDetailModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Detalle del Registro de Auditoría
          </DialogTitle>
          <DialogContent>
            {selectedLog && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Información Básica
                  </Typography>
                  <Typography variant="body2">
                    <strong>ID:</strong> {selectedLog.id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fecha:</strong> {formatearFecha(selectedLog.fecha)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Usuario:</strong> {selectedLog.usuario ? 
                      `${selectedLog.usuario.nombre} ${selectedLog.usuario.apellidos} (${selectedLog.usuario.username})` : 
                      'Desconocido'
                    }
                  </Typography>
                  <Typography variant="body2">
                    <strong>Acción:</strong> {selectedLog.accion}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tabla:</strong> {selectedLog.tabla || 'No especificada'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Registro ID:</strong> {selectedLog.registro_id || 'No especificado'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Información Técnica
                  </Typography>
                  <Typography variant="body2">
                    <strong>IP:</strong> {selectedLog.ip || 'No registrada'}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <strong>User Agent:</strong> {selectedLog.user_agent || 'No registrado'}
                  </Typography>
                </Grid>
                {selectedLog.datos_nuevos && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Datos de la Operación
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <pre style={{ 
                        whiteSpace: 'pre-wrap', 
                        wordBreak: 'break-word',
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                        margin: 0
                      }}>
                        {JSON.stringify(selectedLog.datos_nuevos, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetailModal(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AuditoriaList;